import React, { FC, useEffect, useState } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { GoogleMap } from '@react-google-maps/api';

import { getItem, setItem } from '../../lib/utils/local-storage';
import { getLocationFromAddress, getCurrentLocation, checkMarkerContains } from '../../lib/utils/map';
import { useMap } from '../../lib/hooks/map';
import { useSearchQuery } from '../../lib/hooks/search-query';
import { useSpots } from '../../lib/spots';
import { UserPage } from '../../components/UserPage';
import { Map } from '../../components/Map';
import { MapMenuBar } from '../../components/Map/MapMenuBar';
import { Flash } from '../../components/Flash';


const CurrentLocationButton: FC<{ onClick?: (coordinate: Coordinate | null) => void }> = ({ onClick }) => {
    const [show, setShow] = useState(false);

    async function onButtonClick() {
        const result = await getCurrentLocation();
        if (onClick) {
            onClick(result);
        };
        if (result || show) return;
        setShow(true);
        await new Promise<void>((resolve) => {
            setTimeout(() => {
                resolve();
            }, 1000 * 16);
        });
        setShow(false);
    };

    useEffect(() => {
        async function initialize() {
            const result = await getCurrentLocation();
            setShow(!result);
            await new Promise<void>((resolve) => {
                setTimeout(() => {
                    resolve();
                }, 1000 * 16);
            });
            setShow(false);
        };
        initialize();
    }, []);

    return (
        <>
            <button
                type="button"
                onClick={onButtonClick}
            >
                <img src="/img/icon_target.png" alt="現在地を表示" />
            </button>
            {show && (
                <div className="tooltip">
                    位置情報を取得できませんでした。<Link to="/map/help/geolocation"><a>詳しくはこちら</a></Link>
                </div>
            )}

            <style jsx>{`
                button {
                    cursor: pointer;
                    background: #fff;
                    position: absolute;
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    bottom: 45px;
                    right: 30px;
                    padding: 8px;
                    outline: none;
                    border: solid 2px #f7f7f7;
                    box-shadow: 0 0 8px gray;
                }
                button > img {
                    width: 38px;
                    height: 38px;
                }
                @keyframes fade-out {
                    0% {
                        opacity: 1;
                    }
                    100% {
                        opacity: 0;
                    }
                }
                .tooltip {
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    height: 58px;
                    border: solid 2px #f7f7f7;
                    padding: 8px 14px 8px 14px;
                    position: absolute;
                    bottom: 30px;
                    right: 100px;
                    background: #fff;
                    box-shadow: 0 0 8px gray;
                    font-size: 14px;
                    animation-name: fade-out;
                    animation-duration: 1s;
                    animation-delay: 15s;
                    animation-fill-mode: forwards;
                }
                a {
                    color: #ff9240;
                }
                @media screen and (max-width: 540px) {
                    button {
                        width: 42px;
                        height: 42px;
                    }
                    button > img {
                        width: 22px;
                        height: 22px;
                    }
                    .tooltip {
                        right: 80px;
                        height: 42px;
                        font-size: 11px;
                    }
                }
            `}</style>
        </>
    );
};


export const MapPage: FC = () => {
    const location = useLocation<Coordinate & { flash: string }>();
    const [defaultCenter, setDefaultCenter] = useState<Coordinate | null>(
        location.state?.lat && location.state?.lng
            ? { lat: location.state.lat, lng: location.state.lng }
            : null,
    );
    const [spots, setSpots] = useState<Spots | null>(null);
    const [isInitialized, setIsInitialized] = useState(false);
    const [show, setShow] = useState(false);
    const { map } = useMap();
    const { query, setQuery } = useSearchQuery();
    const { all, search } = useSpots();
    const [searching, setSearching] = useState(false);

    const getLastLocation = () => {
        const locInfo = getItem('lastLocation');
        if (!locInfo) {
            return null;
        }
        const info = JSON.parse(locInfo);
        const now = (new Date()).getTime();
        const period = 24 * 60 * 1000;
        const expired = (info.saved + period) < now;
        return (info.loc && !expired) ? info.loc : null;
    };

    const saveLastLocation = (loc: Coordinate | null) => {
        if (loc) {
            const saved = (new Date()).getTime();
            const locInfo = { loc, saved };
            setItem('lastLocation', JSON.stringify(locInfo));
        }
    };

    const replaceUrl = (data: { area: string, genre: string, keyword: string }) => {
        const history = window.history;
        const url = `${window.location.pathname}?genre=${data.genre}&area=${data.area}&keyword=${data.keyword}`;
        history.replaceState({}, '', url);
    };

    async function onSubmit(data: { area: string, genre: string, keyword: string }) {
        setSearching(true);
        if (data.area !== "") {
            const loc = await getLocationFromAddress(data.area);
            // @ts-ignore
            map.setCenter(loc);
        };
        const loc: Coordinate = {
            // @ts-ignore
            lat: map.center.lat(),
            // @ts-ignore
            lng: map.center.lng(),
        };
        const res = await search({
            ...loc,
            ...data,
        });

        replaceUrl(data);

        if (map && res && res.length > 0) {
            const isNearestContain = res.some((x) => {
                return checkMarkerContains({ lat: x.lat, lng: x.lng }, map);
            });
            if (!isNearestContain) {
                // @ts-ignore
                map.setCenter({ lat: res[0].lat, lng: res[0].lng });
            };
        };
        setTimeout(() => {
            setSearching(false);
        }, 300);
        setSpots(null);
        setSpots(res);
    };

    async function onIdle(map: GoogleMap) {
        if (searching) {
            return false;
        };
        setQuery(prev => ({
            ...prev,
            area: '',
        }));
        const loc: Coordinate = {
            // @ts-ignore
            lat: map.center.lat(),
            // @ts-ignore
            lng: map.center.lng(),
        };
        saveLastLocation(loc);
        const res = await search({
            ...loc,
            ...query,
            area: '',
        });

        // 移動とかズームでURLが変わった方がいいならコメント解除
        // replaceUrl({...query, area: ''});

        setSpots((prev) => {
            if (!res || res.length < 0) return prev;
            if (prev === null && res.length < 0) return prev;
            const newSpots = res.filter((spot) => {
                if (prev === null) return true;
                const hasSpot = prev.every((v) => {
                    return v.id !== spot.id;
                });
                return hasSpot;
            });
            if (prev === null) return [...newSpots];
            return [...prev, ...newSpots];
        });
    };

    function onBoundsChanged(map: GoogleMap) {
        const MINIMUM_ZOOM_LEVEL = 21;
        if (!(spots && spots.length > 0 && !isInitialized)) return;
        // @ts-ignore
        for (let i = 0; i < (MINIMUM_ZOOM_LEVEL - map.getZoom()); i++) {
            const marker = {
                lat: spots[0].lat,
                lng: spots[0].lng,
            };
            if (!checkMarkerContains(marker, map)) {
                // @ts-ignore
                map.setZoom(map.getZoom() - 1);
            } else {
                setIsInitialized(!isInitialized);
                break;
            };
        };
    };

    useEffect(() => {
        const initialize = async () => {
            const currentLocationFromApi = await getCurrentLocation();
            if (!currentLocationFromApi) {
                setShow(true);
            };
            const currentLocation = defaultCenter
                ?? await getLastLocation()
                ?? await getCurrentLocation()
                ?? { lat: 35.6598003, lng: 139.7023894, };
            if (!defaultCenter) setDefaultCenter(currentLocation);
        };
        initialize();
    }, []);

    return (
        <UserPage>
            {map && <MapMenuBar onSubmit={(data) => onSubmit(data)} />}
            <Flash flash={location.state?.flash ?? ''} />
            <Map
                zoom={15}
                center={defaultCenter}
                onIdle={onIdle}
                onBoundsChanged={onBoundsChanged}
                floatingActionButton={(
                    <CurrentLocationButton onClick={(location) => {
                        if (location) {
                            // @ts-ignore
                            map.setCenter(location);
                        } else {
                            setShow(true);
                        };
                    }} />
                )}
                spots={spots}
                styles={[
                    {
                        "featureType": "administrative",
                        "stylers": [
                            {
                                "visibility": "off"
                            },
                        ],
                    },
                    {
                        "featureType": "landscape.man_made",
                        "elementType": "labels",
                        "stylers": [
                            {
                                "visibility": "off"
                            },
                        ],
                    },
                    {
                        "featureType": "poi",
                        "stylers": [
                            {
                                "visibility": "off"
                            },
                        ],
                    },
                    {
                        "featureType": "transit.line",
                        "elementType": "labels",
                        "stylers": [
                            {
                                "visibility": "off"
                            },
                        ],
                    },
                ]}
            />
        </UserPage>
    );
};
